/**
 * Halloween Ghost Can Interactive Effect (Simplified Version)
 * A custom element that displays a ghost-costumed can that randomly appears
 * at viewport positions and scrolls with the page content
 */

class HalloweenGhostCanSimple extends HTMLElement {
  constructor() {
    super();
    this.currentState = 'hidden';
    this.ghostElement = null;
    this.modalElement = null; // Modal for catch message
    this.timeouts = []; // Track all timeouts to cancel them when ghost is caught
    this.isCaught = false; // Track if ghost has been caught
    this.jsConfetti = null; // JSConfetti instance

    // Configurable settings
    this.settings = {
      fadeInSpeed: 600, // milliseconds
      fadeOutSpeed: 800, // milliseconds
      movementSpeed: 1200, // milliseconds for position transitions
      minVisibleTime: 2000, // minimum time visible (ms)
      maxVisibleTime: 5000, // maximum time visible (ms)
      minHiddenTime: 1000, // minimum time before reappearing (ms)
      maxHiddenTime: 3000, // maximum time before reappearing (ms)
      hideBehindChance: 0.5, // 50% chance to hide behind elements (lower z-index)
      edgePadding: 50, // padding from viewport edges (px)
      ghostWidth: 100, // estimated ghost width (px) - adjust if needed
      ghostHeight: 120, // estimated ghost height (px) - adjust if needed
    };
  }

  connectedCallback() {
    // Wait for DOM to be ready and images to load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    // Cleanup
    this.clearAllTimeouts();
    if (this.ghostElement) {
      this.ghostElement.remove();
    }
    if (this.modalElement) {
      this.modalElement.remove();
    }
  }

  clearAllTimeouts() {
    // Cancel all pending timeouts
    this.timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timeouts = [];
  }

  setTimeout(callback, delay) {
    // Wrapper to track timeouts
    const timeoutId = setTimeout(() => {
      const index = this.timeouts.indexOf(timeoutId);
      if (index > -1) {
        this.timeouts.splice(index, 1);
      }
      callback();
    }, delay);
    this.timeouts.push(timeoutId);
    return timeoutId;
  }

  init() {
    // Dev helper: Check for URL parameter to reset ghost (e.g., ?resetGhost=true)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('resetGhost') === 'true') {
      sessionStorage.removeItem('ghostCaught');
      // Remove the parameter from URL without reloading
      urlParams.delete('resetGhost');
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? '?' + urlParams.toString() : '') +
        window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }

    // Dev helper: Keyboard shortcut to reset ghost (Ctrl+Shift+G or Cmd+Shift+G)
    document.addEventListener(
      'keydown',
      (e) => {
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'G') {
          sessionStorage.removeItem('ghostCaught');
          // console.log('👻 Ghost reset! Refresh the page to see it again.');
          alert('Ghost reset! Refresh the page to see it again.');
        }
      },
      { once: false }
    );

    // Check if ghost was already caught in this session
    if (sessionStorage.getItem('ghostCaught')) {
      return; // Don't initialize if already caught
    }

    // Create and append ghost-can element
    this.createGhostElement();

    // Wait for images to load (lazy loading)
    this.waitForContent(() => {
      //console.log('👻 Ghost-can (simplified) initialized');

      // Initialize confetti if library is available
      this.initializeConfetti();

      // Start the animation cycle
      this.startAnimation();
    });
  }

  waitForContent(callback) {
    // Wait for initial images to load
    if (document.readyState === 'complete') {
      setTimeout(callback, 500); // Give lazy-load a chance
    } else {
      window.addEventListener('load', () => {
        setTimeout(callback, 500);
      });
    }
  }

  initializeConfetti() {
    // Check if JSConfetti is available (library might load asynchronously)
    if (typeof JSConfetti !== 'undefined') {
      this.jsConfetti = new JSConfetti();
      // console.log('👻 Confetti initialized');
    } else {
      // Try again after a short delay in case library is still loading
      setTimeout(() => {
        if (typeof JSConfetti !== 'undefined') {
          this.jsConfetti = new JSConfetti();
          // console.log('👻 Confetti initialized (delayed)');
        } else {
          // console.log('👻 Confetti library not available');
        }
      }, 500);
    }
  }

  createGhostElement() {
    this.ghostElement = document.createElement('div');
    this.ghostElement.className = 'halloween-ghost-can';
    this.ghostElement.setAttribute('role', 'button');
    this.ghostElement.setAttribute('aria-label', 'Click to catch the ghost!');
    this.ghostElement.setAttribute('tabindex', '-1'); // Start hidden from tab order

    // Create image element
    const img = document.createElement('img');
    // The image path will be set dynamically via the data-image-url attribute
    const imageUrl = this.getAttribute('data-image-url');
    if (imageUrl) {
      img.src = imageUrl;
      // console.log('👻 Ghost image URL:', imageUrl);
    } else {
      // console.log('👻 No ghost image URL found, using fallback');
      // Fallback: try to construct the URL
      img.src = window.Shopify?.cdnUrl
        ? `${window.Shopify.cdnUrl}/halloween-ghost-can.png`
        : '/assets/halloween-ghost-can.png';
    }
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.loading = 'lazy';
    img.style.width = '100%';
    img.style.height = 'auto';

    this.ghostElement.appendChild(img);

    // Set initial position (off-screen)
    this.ghostElement.style.position = 'absolute';
    this.ghostElement.style.left = '-300px';
    this.ghostElement.style.top = '-300px';

    // Add click and keyboard handlers
    this.ghostElement.addEventListener('click', (e) => this.handleClick(e));
    this.ghostElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleClick(e);
      }
    });

    document.body.appendChild(this.ghostElement);
  }

  getRandomViewportPosition() {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    // Calculate available area (viewport minus padding and ghost size)
    const padding = this.settings.edgePadding;
    const minX = scrollX + padding;
    const maxX = scrollX + viewportWidth - padding - this.settings.ghostWidth;
    const minY = scrollY + padding;
    const maxY = scrollY + viewportHeight - padding - this.settings.ghostHeight;

    // Generate random position within available area
    const x = minX + Math.random() * (maxX - minX);
    const y = minY + Math.random() * (maxY - minY);

    // Determine if ghost should hide behind elements
    const hidesBehind = Math.random() < this.settings.hideBehindChance;

    return { x, y, hidesBehind };
  }

  startAnimation() {
    // Don't start if already caught
    if (this.isCaught) return;

    // Wait a random amount before first appearance
    const delay =
      this.settings.minHiddenTime +
      Math.random() * (this.settings.maxHiddenTime - this.settings.minHiddenTime);

    /*
    console.log('👻 Animation cycle started (simplified)', {
      initialDelay: Math.round(delay),
      settings: this.settings,
    });
    */
    this.setTimeout(() => {
      this.showNextPosition();
    }, delay);
  }

  showNextPosition() {
    // Don't continue if ghost was caught
    if (this.isCaught) return;
    if (this.currentState !== 'hidden') return;

    const position = this.getRandomViewportPosition();

    // Console log the position
    /*
    console.log('👻 Ghost moving to new position:', {
      position: {
        x: Math.round(position.x),
        y: Math.round(position.y),
        hidesBehind: position.hidesBehind,
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      scroll: {
        x: window.pageXOffset,
        y: window.pageYOffset,
      },
    });
    */
    // Set z-index based on whether hiding behind or in front
    if (position.hidesBehind) {
      this.ghostElement.style.zIndex = '100'; // Behind most content
      /*
      console.log('👻 Hiding behind elements', {
        zIndex: '100',
        position: { x: Math.round(position.x), y: Math.round(position.y) },
      });
      */
    } else {
      this.ghostElement.style.zIndex = '9998'; // In front
      /*
      console.log('👻 Appearing in front', {
        zIndex: '9998',
        position: { x: Math.round(position.x), y: Math.round(position.y) },
      });
      */
    }

    // Apply movement speed transition
    this.ghostElement.style.transition = `
      left ${this.settings.movementSpeed}ms ease-in-out,
      top ${this.settings.movementSpeed}ms ease-in-out,
      opacity ${this.settings.fadeInSpeed}ms ease,
      transform 0.3s ease
    `;

    // Set position
    this.ghostElement.style.left = `${position.x}px`;
    this.ghostElement.style.top = `${position.y}px`;

    // Start fading in
    this.setState('appearing');

    // Announce to screen readers when ghost appears
    this.announceToScreenReader('Ghost appeared! Press Enter or Space to catch it.');

    // After fade in, stay visible for a bit
    this.setTimeout(() => {
      if (this.isCaught) return;
      this.setState('visible');

      // Then start fading out after random visible time
      const visibleTime =
        this.settings.minVisibleTime +
        Math.random() * (this.settings.maxVisibleTime - this.settings.minVisibleTime);

      this.setTimeout(() => {
        if (this.isCaught) return;
        this.setState('fading');

        // After fade out, move to next position
        this.setTimeout(() => {
          if (this.isCaught) return;
          this.setState('hidden');

          // Random delay before next appearance
          const hiddenTime =
            this.settings.minHiddenTime +
            Math.random() * (this.settings.maxHiddenTime - this.settings.minHiddenTime);

          this.setTimeout(() => {
            this.showNextPosition();
          }, hiddenTime);
        }, this.settings.fadeOutSpeed);
      }, visibleTime);
    }, this.settings.fadeInSpeed);
  }

  setState(state) {
    this.currentState = state;
    this.ghostElement.className = `halloween-ghost-can state-${state}`;

    // Manage tabindex for keyboard accessibility
    // Visible states should be keyboard accessible, hidden state should not be
    if (state === 'visible' || state === 'appearing') {
      this.ghostElement.setAttribute('tabindex', '0');
    } else {
      this.ghostElement.setAttribute('tabindex', '-1');
    }

    // Console logging for debugging
    const currentPos = {
      x: this.ghostElement.style.left,
      y: this.ghostElement.style.top,
      zIndex: this.ghostElement.style.zIndex,
      opacity: window.getComputedStyle(this.ghostElement).opacity,
      display: window.getComputedStyle(this.ghostElement).display,
      visibility: window.getComputedStyle(this.ghostElement).visibility,
    };
    const rect = this.ghostElement.getBoundingClientRect();
    /*
    console.log(`👻 Ghost state: ${state}`, {
      style: currentPos,
      boundingRect: {
        left: Math.round(rect.left),
        top: Math.round(rect.top),
        right: Math.round(rect.right),
        bottom: Math.round(rect.bottom),
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      },
      inViewport:
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.right <= window.innerWidth,
    });
    */
    // Update transition timing for fade states
    if (state === 'appearing') {
      this.ghostElement.style.transition = `opacity ${this.settings.fadeInSpeed}ms ease, transform 0.3s ease`;
    } else if (state === 'fading') {
      this.ghostElement.style.transition = `opacity ${this.settings.fadeOutSpeed}ms ease, transform 0.3s ease`;
    }
  }

  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    // Don't do anything if already caught
    if (this.isCaught) return;

    const rect = this.ghostElement.getBoundingClientRect();
    /*
    console.log('👻 Ghost caught!', {
      position: {
        x: Math.round(rect.left),
        y: Math.round(rect.top),
        styleLeft: this.ghostElement.style.left,
        styleTop: this.ghostElement.style.top,
      },
      state: this.currentState,
    });
    */
    // Mark as caught immediately
    this.isCaught = true;

    // Cancel all pending animation timeouts
    this.clearAllTimeouts();

    // Store that ghost was caught in session storage
    sessionStorage.setItem('ghostCaught', 'true');

    // Visual feedback
    this.ghostElement.style.transform = 'scale(0.9)';
    this.setTimeout(() => {
      this.ghostElement.style.transform = 'scale(1)';
    }, 100);

    // Announce to screen readers
    this.announceToScreenReader('You caught the ghost! Happy Halloween!');

    // Trigger confetti animation
    this.triggerConfetti();

    // Hide immediately with fade out transition
    this.ghostElement.style.transition = `opacity ${this.settings.fadeOutSpeed}ms ease`;
    this.ghostElement.style.opacity = '0';

    // Remove from DOM after fade completes
    this.setTimeout(() => {
      this.setState('hidden');
      if (this.ghostElement) {
        this.ghostElement.style.display = 'none';
      }
    }, this.settings.fadeOutSpeed);

    // Show accessible modal
    this.showCatchModal();
  }

  triggerConfetti() {
    // Use js-confetti with Halloween emojis (launches from screen sides by default)
    if (!this.jsConfetti) {
      return;
    }

    const icon1 = this.getAttribute('data-image-icon-1-url');
    const icon2 = this.getAttribute('data-image-icon-2-url');
    const icon3 = this.getAttribute('data-image-icon-3-url');

    const icons = [icon1, icon2, icon3];

    /*
    console.log('👻 Confetti icons:', icons);
    */

    const confettiImages = [];
    icons.forEach((icon) => {
      if (icon) {
        const iconUrl = 'https:' + icon;
        confettiImages.push({ src: iconUrl, width: 32, height: 32 });
      }
    });
    /*
    console.log('👻 Confetti images:', confettiImages);
    */
    const temp = {
      //images: confettiImages,
      emojis: ['👻', '🎃', '🦇', '🕷️', '🕸️'],
      emojiSize: 150,
      confettiNumber: 40,
      confettiRadius: 12,
    };
    /*
    console.log('👻 Confetti temp:', temp);
    */
    try {
      // Use Halloween-themed emojis
      this.jsConfetti.addConfetti({
        //images: confettiImages,
        emojis: ['👻', '🎃', '🦇', '🕷️', '🕸️'],
        emojiSize: 150,
        confettiNumber: 40,
        confettiRadius: 12,
      });

      // Trigger additional bursts with slight delay for layered effect
      setTimeout(() => {
        if (this.jsConfetti) {
          this.jsConfetti.addConfetti({
            emojis: ['👻', '🎃', '🦇', '🕷️', '🕸️'],
            emojiSize: 120,
            confettiNumber: 30,
            confettiRadius: 12,
          });
        }
      }, 400);

      setTimeout(() => {
        if (this.jsConfetti) {
          this.jsConfetti.addConfetti({
            emojis: ['👻', '🎃'],
            emojiSize: 150,
            confettiNumber: 20,
            confettiRadius: 12,
          });
        }
      }, 600);
    } catch (error) {
      // console.warn('Failed to trigger confetti:', error);
    }
  }

  createCatchModal() {
    // Create modal HTML structure
    const modal = document.createElement('div');
    modal.className = 'halloween-ghost-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'halloween-ghost-modal-title');
    modal.setAttribute('aria-describedby', 'halloween-ghost-modal-description');

    // Get ghost image URL
    const ghostImageUrl =
      this.getAttribute('data-image-url') ||
      (window.Shopify?.cdnUrl
        ? `${window.Shopify.cdnUrl}/halloween-ghost-can.png`
        : '/assets/halloween-ghost-can.png');

    modal.innerHTML = `
      <div class="halloween-ghost-modal__overlay"></div>
      <div class="halloween-ghost-modal__container" role="document">
        <button 
          class="halloween-ghost-modal__close" 
          aria-label="Close modal"
          type="button"
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <div class="halloween-ghost-modal__content">
          <h2 class="halloween-ghost-modal__title" id="halloween-ghost-modal-title">
            Boo! 👻 You caught the ghost can!<br>Happy Halloween! 🎃
          </h2>
          <div class="halloween-ghost-modal__image-wrapper" id="halloween-ghost-modal-description">
            <img 
              src="${ghostImageUrl}" 
              alt="Halloween ghost can" 
              class="halloween-ghost-modal__image"
            >
          </div>
        </div>
      </div>
    `;

    // Add event listeners
    const closeButton = modal.querySelector('.halloween-ghost-modal__close');
    const overlay = modal.querySelector('.halloween-ghost-modal__overlay');

    closeButton.addEventListener('click', () => this.hideCatchModal());
    overlay.addEventListener('click', () => this.hideCatchModal());

    // Close on Escape key
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hideCatchModal();
      }
    });

    this.modalElement = modal;
    document.body.appendChild(modal);

    return modal;
  }

  showCatchModal() {
    // Create modal if it doesn't exist
    if (!this.modalElement) {
      this.createCatchModal();
    }

    // Show modal
    this.modalElement.classList.add('halloween-ghost-modal--open');
    document.body.classList.add('overflow-hidden');

    // Trap focus
    const modalContainer = this.modalElement.querySelector('.halloween-ghost-modal__container');
    if (typeof trapFocus === 'function') {
      trapFocus(modalContainer, this.modalElement.querySelector('.halloween-ghost-modal__close'));
    } else {
      // Fallback: focus the close button
      this.modalElement.querySelector('.halloween-ghost-modal__close').focus();
    }

    // Auto-close after 8 seconds (allows more time for reading)
    this.setTimeout(() => {
      this.hideCatchModal();
    }, 8000);
  }

  hideCatchModal() {
    if (!this.modalElement) return;

    this.modalElement.classList.remove('halloween-ghost-modal--open');
    document.body.classList.remove('overflow-hidden');

    // Remove focus trap
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }
  }

  announceToScreenReader(message) {
    // Create or reuse a live region for announcements
    let liveRegion = document.getElementById('ghost-announcements');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'ghost-announcements';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'sr-only';
      liveRegion.style.cssText =
        'position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border-width: 0;';
      document.body.appendChild(liveRegion);
    }

    // Clear previous message and announce new one
    liveRegion.textContent = '';
    setTimeout(() => {
      liveRegion.textContent = message;
    }, 100);
  }
}

// Register the custom element
customElements.define('halloween-ghost-can-simple', HalloweenGhostCanSimple);
